import React, { Component } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import camelCase from 'lodash/camelCase';
import { getTranslatedString } from 'helpers/i18n';

import StatusTag from 'components/ui/StatusTag';
import CtaCard from 'components/ui/CtaCard';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { Form as AntdForm } from 'antd';

import MaintenanceTicketSearch from 'components/shared/MaintenanceTicketSearch';
import TableWithoutFilters from 'components/ui/TableWithoutFilters';

const FormTitle = styled.div`
  font-size: 20px;
  font-family: 'Circular-Bold';
  margin-bottom: 1rem;
`;

const ScrollablePanel = styled.div`
  height: 100%;
  overflow: scroll;
`;

const StyledCtaCard = styled(CtaCard)`
  left: 0;
  right: 0;
  position: absolute;
  margin: auto;
  margin-top: -13px;
`;

const fieldsAboveFold = [
  {
    title: getTranslatedString('landlordPortal.landlordMaintenance.unitNo'),
    key: 'unit_number',
  },
  {
    title: getTranslatedString('landlordPortal.landlordMaintenance.priority'),
    key: 'priority',
  },
];

const fieldsBelowFold = (handleUnlinkServiceRequest) => {
  return [
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.requestType'),
      key: 'request_type',
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.scheduledDate'),
      key: 'scheduled_starts_at',
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.submittedDate'),
      key: 'request_date',
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.lastUpdated'),
      key: 'last_updated',
    },
    {
      title: getTranslatedString('landlordPortal.landlordMaintenance.assignee'),
      key: 'assigned_to',
    },
    {
      title: '',
      key: 'id',
      renderer: (data) => {
        return (
          <StyledCtaCard type="button" onClick={() => handleUnlinkServiceRequest(data)}>
            {getTranslatedString('landlordPortal.maintenance.projects.unlink')}
          </StyledCtaCard>
        );
      },
    },
  ];
};

class ProjectTickets extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sortKey: '',
      sortOrder: '',
      offset: 50, // We've already fetched in the parent on did mount for the initial 50 values
    };
  }

  componentDidMount() {
    const { projectID, fetchFilteredProjectTickets } = this.props;
    fetchFilteredProjectTickets(projectID, 0, { sortKey: '', sortOrder: '' });
  }

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => this.fetchFilteredData()
    );
    if (this.props.tableRef) this.props.tableRef.scrollToTop(0);
  };

  fetchFilteredData = async () => {
    const { projectID, fetchFilteredProjectTickets } = this.props;
    const { offset, sortKey, sortOrder } = this.state;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    const fetchPromise = fetchFilteredProjectTickets(projectID, offset, filterOptions);

    if (this.props.fetchStats) {
      this.props.fetchStats();
    }
    await fetchPromise;
    const self = this;
    this.setState({ offset: self.props.tickets.length });
  };

  handleSelectServiceRequest = async (serviceRequest) => {
    const { projectID, linkTicketToProject, fetchFilteredProjectTickets, fetchProject } =
      this.props;
    const maintenanceTicketID = serviceRequest.id;
    await linkTicketToProject(projectID, maintenanceTicketID);
    fetchFilteredProjectTickets(projectID, 0, {});
    fetchProject(projectID); // Re-fetch the number of linked tickets
  };

  handleUnlinkServiceRequest = async (serviceRequestId) => {
    const { projectID, unlinkTicketFromProject, fetchProject } = this.props;
    await unlinkTicketFromProject(projectID, serviceRequestId);
    fetchProject(projectID); // Re-fetch the number of linked tickets
  };

  render() {
    const { projectID, propertyID, tickets, currentLanguage } = this.props;

    const { offset, sortKey, sortOrder } = this.state;

    const columns = [
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.ticketNo'),
        dataKey: 'ticket_number',
        sortable: true,
        width: 150,
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.status'),
        dataKey: 'status',
        sortable: false,
        width: 200,
        render: ({ data }) => <StatusTag status={data} />,
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.unit'),
        dataKey: 'unit_number',
        sortable: true,
        width: 150,
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.subject'),
        dataKey: 'subject',
        sortable: true,
        width: 400,
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.requestType'),
        dataKey: 'request_type',
        sortable: false,
        width: 300,
        render: ({ data }) => {
          if (!data) return '-';
          data = data.replace(/tenant/gi, 'Resident');
          return getTranslatedString(`landlordPortal.landlordMaintenance.${camelCase(data)}`);
        },
      },
      {
        title: getTranslatedString('landlordPortal.landlordMaintenance.scheduledDate'),
        dataKey: 'scheduled_starts_at',
        sortable: true,
        width: 200,
        render: ({ data }) => {
          const date = moment(data);
          if (date.isValid()) return date.locale(currentLanguage || 'en').fromNow();
          return getTranslatedString('landlordPortal.landlordMaintenance.notSchedule');
        },
      },
      {
        title: '',
        dataKey: 'original_id',
        sortable: false,
        width: 200,
        render: ({ data }) => (
          <CtaCard type="button" onClick={() => this.handleUnlinkServiceRequest(data)}>
            {getTranslatedString('landlordPortal.maintenance.projects.unlink')}
          </CtaCard>
        ),
      },
    ];

    let tableRef = null;
    const setRef = (ref) => (tableRef = ref);
    const selectedServiceRequests = tickets.map((ticket) => ticket.id);

    return (
      <ScrollablePanel>
        <Row gutter={16}>
          <Col md={12} xs={24} style={{ maxWidth: '550px' }}>
            <Spacer pt={1} pr={2} pl={2}>
              <FormTitle>
                {getTranslatedString('landlordPortal.linkServiceRequests.linkServiceRequests')}
              </FormTitle>
              <AntdForm layout="vertical">
                <Form.Item
                  label={getTranslatedString('landlordPortal.linkServiceRequests.serviceRequests')}
                >
                  <MaintenanceTicketSearch
                    propertyIds={propertyID}
                    handleSelectItem={this.handleSelectServiceRequest}
                    selectedServiceRequests={selectedServiceRequests}
                  />
                </Form.Item>
              </AntdForm>
            </Spacer>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24}>
            <Spacer mt={0}>
              <TableWithoutFilters
                data={tickets}
                fetchFilteredData={this.fetchFilteredData}
                loadMoreData={this.fetchFilteredData}
                fetchStats={() => this.props.fetchProject(projectID)}
                isLoading={false}
                columns={columns}
                emptyTableText={getTranslatedString(
                  'landlordPortal.maintenance.projects.noLinkedTickets'
                )}
                fieldsAboveFold={fieldsAboveFold}
                fieldsBelowFold={fieldsBelowFold(this.handleUnlinkServiceRequest)}
                mobileTableTitle="subject"
                rowHeader="ticket_number"
                setRef={setRef}
                tableRef={tableRef}
                offset={offset}
                sortKey={sortKey}
                sortOrder={sortOrder}
                handleSort={this.handleSort}
              />
            </Spacer>
          </Col>
        </Row>
      </ScrollablePanel>
    );
  }
}

export default ProjectTickets;
